// WHAT A TYPED SEARCH MATCHES in `MultiSelectField`'s held list (owner,
// 2026-09-25 — docs/decisions.md D-182).
//
// An option matches on its LABEL, which is its name and what the list shows
// first. An option that carries an ID (`secondary`) is matched the way the
// References and Rules → Codes pickers match a half — `halfMatchesSearch` in
// `lib/codeId.ts`, so the two cannot drift: the name, any alternative wordings,
// and the ID with or without its dashes. That is what lets the Rules →
// Oversight Approval Account, Department and Code pickers find `66-549` (Food
// & Staples) and `689309-66-549` (its code) while each row still reads as its
// name. An option without an ID matches on its label alone, exactly as before,
// so the Dashboard and Reporting filters do not change.
//
// A module of its own because a component module may export only components,
// and because this suite has no DOM to type into the real control.
import { searchRank } from '@ap/shared'
import { halfMatchesSearch } from './codeId'

interface SearchableOption {
  label: string
  /** The option's ID, shown under its name. */
  secondary?: string
  /** Alternative wordings the option also answers to (`search_terms`). */
  searchTerms?: string | null
}

export function optionMatchesSearch(option: SearchableOption, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (needle === '') return true
  if (option.secondary === undefined) return option.label.toLowerCase().includes(needle)
  return halfMatchesSearch({ id: option.secondary, name: option.label, searchTerms: option.searchTerms }, query)
}

export function filterOptions<T extends SearchableOption>(options: readonly T[], query: string | null): T[] {
  const term = query ?? ''
  return rankBySearch(
    options.filter((o) => optionMatchesSearch(o, term)),
    term,
    (o) => [o.label, o.secondary],
  )
}

// ---- HOW A TYPED SEARCH ORDERS ITS MATCHES (QA 2026-10-04, C1-04) -----------
//
// Exact, then starts-with, then a word starts with it, then anything else that
// matched — by name or by ID, whichever ranks better. Typing `Supplies`
// listed `Incontinence Supplies` first, because the list came back in name
// order, and Enter took it. Every picker orders its matches through this one
// rule: `EntityPicker` on the rows the server returns, `MultiSelectField`
// through `filterOptions` above. Ties keep the order they arrived in, so the
// master's own order survives inside each rank.

/** 0 exact, 1 starts with, 2 a word starts with, 3 any other match — the
    shared rule, which the server's vendor search applies too (D-296). */
export { searchRank } from '@ap/shared'

/** `options`, best match first; stable within a rank. Unchanged for an empty query. */
export function rankBySearch<T>(
  options: readonly T[],
  query: string | null | undefined,
  candidates: (option: T) => readonly (string | null | undefined)[],
): T[] {
  if (!query || query.trim() === '') return [...options]
  return options
    .map((option, index) => ({ option, index, rank: searchRank(candidates(option), query) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((r) => r.option)
}
